import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import measurementService from '../../../../services/measurementService';
import patientService from '../../../../services/patientService';
import { toast } from 'react-toastify';
import EmailModal from '../../../../components/EmailModal/EmailModal';
import emailService from '../../../../services/emailService';
import { exportElementToPdf, printElement } from '../../../../utils/reportExportUtils';
import { getPresetDateRangeParams } from '../../../../utils/measurementUtils';
import { PATIENT_SCHEDULE_TARGET_PREF_EVENT } from '../../../../utils/patientScheduleTargetPreference';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import MeasurementChartHeader from '../../ChartHeader/MeasurementChartHeader';

import DailyTrendChartWeight from './DailyTrendChartWeight';
import DailyTrendChartFat from './DailyTrendChartFat';
import DailyTrendChartBMI from './DailyTrendChartBMI';
import MeanChartWeight from './MeanChartWeight';
import MeanChartFat from './MeanChartFat';
import PieChartWeight from './PieChartWeight';
import PieChartFat from './PieChartFat';

import './WeightGraphs.css';

const WeightGraphs = () => {
    const { practiceId, patientId } = useParams();

    const [queryType, setQueryType] = useState('dateRange');
    const [dateRange, setDateRange] = useState('Last 30 days');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const cacheKey = `chart_patient_meta_${practiceId}_${patientId}`;
    const [patientData, setPatientData] = useState(() => {
        try {
            const raw = sessionStorage.getItem(cacheKey);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    const [measurements, setMeasurements] = useState([]);
    const [scheduleTargets, setScheduleTargets] = useState(null);

    const [loading, setLoading] = useState(true);
    const [queryLoading, setQueryLoading] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const { updateBreadcrumbs } = useBreadcrumb();

    // Fetch patient data and schedule targets on mount
    useEffect(() => {
        if (practiceId && patientId) {
            setQueryType('dateRange');
            setDateRange('Last 30 days');
            setFromDate('');
            setToDate('');
            fetchPatientData();
            fetchScheduleTargets();
        }
    }, [practiceId, patientId]);

    useEffect(() => {
        if (!practiceId || !patientId) return undefined;
        const onPref = (ev) => {
            const d = ev?.detail;
            if (String(d?.practiceId) !== String(practiceId) || String(d?.patientId) !== String(patientId)) return;
            fetchScheduleTargets();
        };
        window.addEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
        return () => window.removeEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
    }, [practiceId, patientId]);

    // Fetch measurements when query parameters change
    useEffect(() => {
        if (practiceId && patientId) {
            fetchMeasurements();
        }
    }, [practiceId, patientId, dateRange, fromDate, toDate]);

    // Breadcrumbs effect
    useEffect(() => {
        if (patientData?.patient && practiceId && patientId) {
            updateBreadcrumbs([
                { label: 'Practices', path: '/practices' },
                { label: patientData.patient.practice_name || 'Practice', path: `/practices/${practiceId}` },
                { label: 'Patients', path: `/practices/${practiceId}/patients` },
                { 
                  label: `${patientData.patient.last_name || ''}, ${patientData.patient.first_name || ''}`.replace(/^, | ,$|(, )+/g, '$1').trim() || 'Patient', 
                  path: `/practices/${practiceId}/patients/${patientId}` 
                },
                { label: 'Weight Graphs', path: null, active: true }
            ]);
        }
    }, [patientData, practiceId, patientId, updateBreadcrumbs]);

    const fetchPatientData = async () => {
        try {
            const response = await patientService.getMeasurementHeaderFast(practiceId, patientId);
            if (response.data.success) {
                setPatientData(response.data.data);
                try {
                    sessionStorage.setItem(cacheKey, JSON.stringify(response.data.data));
                } catch {
                    // ignore cache write errors
                }
            }
        } catch (error) {
            console.error('Error fetching patient data:', error);
            toast.error('Failed to load patient data');
        }
    };

    const fetchScheduleTargets = async () => {
        try {
            const data = await patientService.getResolvedScheduleTargetsForPatient(practiceId, patientId);
            setScheduleTargets(data);
        } catch (error) {
            console.error('Error fetching schedule targets:', error);
        }
    };

    const fetchMeasurements = async () => {
        try {
            setQueryLoading(true);
            let fromDateParam, toDateParam;

            if (queryType === 'dateRange') {
                const params = getPresetDateRangeParams(dateRange);
                fromDateParam = params.fromDateParam;
                toDateParam = params.toDateParam;
            } else {
                // Inputs are usually YYYY-MM-DD from type="date"; also support legacy mm/dd/yyyy
                if (fromDate && toDate) {
                    if (fromDate.includes('/')) {
                        const [fromMonth, fromDay, fromYear] = fromDate.split('/');
                        const [toMonth, toDay, toYear] = toDate.split('/');
                        fromDateParam = `${fromYear}-${fromMonth.padStart(2, '0')}-${fromDay.padStart(2, '0')}`;
                        toDateParam = `${toYear}-${toMonth.padStart(2, '0')}-${toDay.padStart(2, '0')}`;
                    } else {
                        fromDateParam = fromDate;
                        toDateParam = toDate;
                    }
                }
            }

            if (fromDateParam && toDateParam) {
                const response = await measurementService.getWeightByDateRange(
                    practiceId,
                    patientId,
                    fromDateParam,
                    toDateParam,
                    'date'
                );
                if (response.data?.success) {
                    setMeasurements(response.data.data || []);
                }
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching measurements:', error);
            if (error.response?.status !== 404) {
                toast.error('Failed to load measurements');
            }
            setMeasurements([]);
            setLoading(false);
        } finally {
            setQueryLoading(false);
        }
    };

    const handleQuery = () => {
        if (queryLoading) return;
        fetchMeasurements();
    };

    const patientName = patientData?.patient
        ? `${patientData.patient.first_name || ''} ${patientData.patient.last_name || ''}`.trim()
        : 'Loading...';
    const patientDOB = patientData?.patient?.date_of_birth
        ? new Date(patientData.patient.date_of_birth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
        : 'Loading...';
    const patientVitals = patientData?.patient?.vitals || '';

    const handleExportPDF = async () => {
        if (measurements.length === 0) {
            toast.warning('No data to export');
            return;
        }
        try {
            const element = document.getElementById('wg-pdf-content');
            await exportElementToPdf(
                element,
                `Weight-Graphs-${patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`
            );
            toast.success('Report exported to PDF successfully');
        } catch (error) {
            console.error('Error exporting PDF:', error);
            toast.error('Failed to export PDF');
        }
    };

    const generateEmailContent = () => {
        if (!measurements || measurements.length === 0) return 'No weight measurement data available for the selected period.';
        const latest = measurements[measurements.length - 1]; // Assuming sorted
        return `Weight Graphs Report\nPatient: ${patientName} (DOB: ${patientDOB})\nGenerated: ${new Date().toLocaleString()}\nTotal Records: ${measurements.length}\n\nLatest Weight: ${latest?.weight != null ? latest.weight : 'N/A'}\nLatest Body Fat: ${latest?.body_fat || 'N/A'}%\nLatest BMI: ${latest?.bmi || 'N/A'}`;
    };

    const handlePrint = () => {
        try {
            const el = document.getElementById('wg-pdf-content');
            printElement(el, `Weight Graphs - ${patientName}`);
        } catch (e) {
            toast.error(e?.message || 'Print failed');
        }
    };

    return (
        <div className="weight-graphs-page">
            <MeasurementChartHeader
                practiceId={practiceId}
                patientId={patientId}
                vitals={patientVitals}
                activeVital="weight"
                patientName={patientName}
                patientDOB={patientDOB}
                queryType={queryType}
                setQueryType={setQueryType}
                dateRange={dateRange}
                setDateRange={setDateRange}
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                onQuery={handleQuery}
                onPrint={handlePrint}
                onExport={handleExportPDF}
                onEmail={() => setShowEmailModal(true)}
                hasData={measurements.length > 0}
            />

            {/* Graph Renderings */}
            <div className="wg-charts-container" id="wg-pdf-content">
                {loading || queryLoading ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
                ) : (
                    <>
                        <fieldset className="wg-fieldset-custom wg-chart-section">
                            <legend className="wg-legend-custom">Standard Day Chart of Weight</legend>
                            <DailyTrendChartWeight measurements={measurements} />
                        </fieldset>
                        <fieldset className="wg-fieldset-custom wg-chart-section">
                            <legend className="wg-legend-custom">Standard Day Chart of Fat</legend>
                            <DailyTrendChartFat measurements={measurements} />
                        </fieldset>
                        <fieldset className="wg-fieldset-custom wg-chart-section">
                            <legend className="wg-legend-custom">Standard Day Chart of BMI</legend>
                            <DailyTrendChartBMI measurements={measurements} />
                        </fieldset>

                        <fieldset className="wg-fieldset-custom wg-mean-chart-wrapper">
                            <legend className="wg-legend-custom">Mean Chart of Weight</legend>
                            <MeanChartWeight measurements={measurements} scheduleTargets={scheduleTargets} />
                        </fieldset>
                        <fieldset className="wg-fieldset-custom wg-mean-chart-wrapper">
                            <legend className="wg-legend-custom">Mean Chart of Fat</legend>
                            <MeanChartFat measurements={measurements} scheduleTargets={scheduleTargets} />
                        </fieldset>

                        <fieldset className="wg-fieldset-custom wg-pie-chart-wrapper">
                            <legend className="wg-legend-custom">Pie Chart of Weight</legend>
                            <PieChartWeight measurements={measurements} scheduleTargets={scheduleTargets} />
                        </fieldset>
                        <fieldset className="wg-fieldset-custom wg-pie-chart-wrapper">
                            <legend className="wg-legend-custom">Pie Chart of Fat</legend>
                            <PieChartFat measurements={measurements} scheduleTargets={scheduleTargets} />
                        </fieldset>
                    </>
                )}
            </div>

            {/* Modals */}
            <EmailModal
                isOpen={showEmailModal}
                onClose={() => setShowEmailModal(false)}
                onSend={async (emailData) => {
                    try {
                        if (emailData.attachments?.length) {
                            toast.info('Attachments are not supported yet. Sending without attachments.');
                        }
                        const payload = {
                            to: emailData.to,
                            cc: emailData.cc,
                            bcc: emailData.bcc,
                            subject: emailData.subject,
                            text: emailData.content,
                            html: `<pre style="font-family: Arial, sans-serif; white-space: pre-wrap;">${String(emailData.content || '').replace(/</g, '&lt;')}</pre>`,
                            event_id: 11
                        };
                        const res = await emailService.sendCustomNotification(payload);
                        if (!res.success) throw new Error(res.error || res.message);
                        toast.success('Email queued successfully');
                        setShowEmailModal(false);
                    } catch (e) {
                        toast.error(e?.message || 'Failed to send email');
                    }
                }}
                defaultSubject={`Weight Graphs Report - ${new Date().toLocaleDateString()}`}
                defaultContent={generateEmailContent()}
            />
        </div>
    );
};

export default WeightGraphs;
